import { NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation, ChangeDetectionStrategy, ViewChild, ElementRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FuseConfigService, Scheme } from '@fuse/services/config';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { UserService } from 'app/core/user/user.service';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Users } from 'app/modal/users';
import { Subject, filter, takeUntil } from 'rxjs';
import { AppSettingService } from "../../../../services/app-setting.service";
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';

import { EventTickerComponent } from 'app/layout/common/event-ticker/event-ticker.component';

@Component({
    selector: 'classy-layout',
    templateUrl: './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FuseLoadingBarComponent, FuseVerticalNavigationComponent, UserComponent, NgClass, MatIconModule, MatButtonModule, FuseFullscreenComponent, SearchComponent, RouterOutlet, MatMenuModule, MatDividerModule, EventTickerComponent]
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
    @ViewChild('scrollArea') scrollArea: ElementRef<HTMLElement>;

    isScreenSmall: boolean;
    isNavigationOpened: boolean = true;
    navigation: Navigation;
    user: Users;
    isScrolled: boolean = false;
    config: any;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseConfigService: FuseConfigService,
        protected appSettingService: AppSettingService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe((takeUntil(this._unsubscribeAll)))
            .subscribe((user: Users) => {
                this.user = user;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        // Subscribe to config changes
        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config: any) => {
                this.config = config;
            });

        // Subscribe to NavigationEnd event to scroll to top
        this._router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe(() => {
                this._scrollToTop();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Sign out
     */
    signOut(): void {
        this._router.navigate(['/sign-out']);
    }

    /**
     * Set the scheme on the config
     *
     * @param scheme
     */
    setScheme(scheme: Scheme): void {
        this._router.navigate([], {
            queryParams: { scheme: null },
            queryParamsHandling: 'merge',
        }).then(() => {
            this._fuseConfigService.config = {scheme};
        });
    }

    /**
     * Set the layout
     */
    setLayout(layout: string): void {
        this._router.navigate([], {
            queryParams: { layout: null },
            queryParamsHandling: 'merge',
        }).then(() => {
            this._fuseConfigService.config = {layout};
        });
    }

    /**
     * On scroll event
     */
    onScroll(event: Event): void {
        const target = event.target as HTMLElement;
        this.isScrolled = target.scrollTop > 0;
    }

    /**
     * Scroll main container to top
     */
    private _scrollToTop(): void {
        this.isScrolled = false;
        const reset = () => {
            if (this.scrollArea?.nativeElement) {
                this.scrollArea.nativeElement.scrollTop = 0;
            }
        };

        reset();
        requestAnimationFrame(reset);
        setTimeout(reset, 50);
    }
}
